/**
 * NOTE (conventions §9): adding a value needs a matching ALTER … MODIFY COLUMN
 * in packages/database/schema-changes/, or MySQL (non-strict) silently
 * truncates writes of the new value to '' and strands the rows; under strict
 * mode (the default, and what production runs) the write 500s instead — see
 * docs/plans/email-account-service-provider.md §0/§5.
 *
 * The 8 members below OTHERS (generic custom SMTP) are cosmetic ESP-brand identification for an
 * SMTP-transport account matched to a cataloged preset (Zoho, Yahoo, SendGrid,
 * Mailgun, Amazon SES, Elastic Email, Yandex, Go Daddy) — they carry NO
 * different transport behavior from custom SMTP (still nodemailer/imapflow,
 * still `usesPasswordTransport`-gated) and NO different pacing behavior
 * (still the `other` ESP bucket in `providerToEspGroup`, see `ESP_BRAND_PROVIDERS`
 * below). Anywhere that needs "is this SMTP-transport" must branch on
 * `usesPasswordTransport(account)`, never on `provider === EmailProvider.OTHERS`
 * — the latter silently excludes every one of these 8 values. See
 * apps/api/src/modules/email-accounts/CLAUDE.md and
 * apps/warmup-worker/CLAUDE.md for the two call sites this bit already.
 */
export enum EmailProvider {
  GMAIL             = 'GMAIL',
  GOOGLE_WORKSPACE  = 'GOOGLE_WORKSPACE',
  OUTLOOK           = 'OUTLOOK',
  MS365             = 'MS365',
  /**
   * A Microsoft mailbox hosted on an Azure tenant — written when Saleshandy's
   * sync reports `emailServiceProvider: 'azure'` (InboxKit mailboxes). Same
   * transport as OUTLOOK/MS365 (Graph + Microsoft OAuth); branch on
   * {@link isMicrosoftProvider}, never on a single Microsoft member.
   */
  AZURE             = 'AZURE',
  OTHERS            = 'OTHERS',
  ZOHO              = 'ZOHO',
  YAHOO             = 'YAHOO',
  SENDGRID          = 'SENDGRID',
  MAILGUN           = 'MAILGUN',
  AMAZON_SES        = 'AMAZON_SES',
  ELASTIC_EMAIL     = 'ELASTIC_EMAIL',
  YANDEX            = 'YANDEX',
  GODADDY           = 'GODADDY',
}

/**
 * True for the Google-family providers — the ones whose mailboxes hold a Google
 * OAuth grant and are served by the Gmail API. Never branch on
 * `provider === EmailProvider.GMAIL` for "is this Google": it silently excludes
 * GOOGLE_WORKSPACE, which then falls into whatever the `else` is (the OAuth
 * refresh strategies sent Workspace refresh tokens to Microsoft's token
 * endpoint and got AADSTS9002313 back).
 */
export function isGoogleProvider(provider: EmailProvider): boolean {
  return provider === EmailProvider.GMAIL || provider === EmailProvider.GOOGLE_WORKSPACE;
}

/**
 * The Microsoft-family providers — mailboxes holding a Microsoft OAuth grant,
 * served by Graph. Never branch on `OUTLOOK || MS365` for "is this
 * Microsoft": it silently excludes AZURE, which then falls into whatever the
 * `else` is (the same failure `isGoogleProvider` documents).
 */
export const MICROSOFT_PROVIDERS: readonly EmailProvider[] = [
  EmailProvider.OUTLOOK,
  EmailProvider.MS365,
  EmailProvider.AZURE,
] as const;

export function isMicrosoftProvider(provider: EmailProvider): boolean {
  return MICROSOFT_PROVIDERS.includes(provider);
}

/**
 * The 8 cosmetic ESP-brand `EmailProvider` members, as a single source of
 * truth other modules key off — the email-accounts filter fix
 * (`EmailAccountsRepository.PROVIDER_MAP`) and the provider-preset resolver
 * (`resolveProvider`) both import this rather than hand-enumerating the list a
 * second time.
 */
export const ESP_BRAND_PROVIDERS: readonly EmailProvider[] = [
  EmailProvider.ZOHO,
  EmailProvider.YAHOO,
  EmailProvider.SENDGRID,
  EmailProvider.MAILGUN,
  EmailProvider.AMAZON_SES,
  EmailProvider.ELASTIC_EMAIL,
  EmailProvider.YANDEX,
  EmailProvider.GODADDY,
] as const;

/**
 * How we authenticate to a mailbox — orthogonal to `EmailProvider`, which says
 * which ESP brand it is. PASSWORD ⇒ SMTP/IMAP with a stored password (Gmail app
 * password, SendGrid API key, Mailgun SMTP password, …) and a pod-owned socket.
 * OAUTH ⇒ bearer token (refreshed, or DWD-minted).
 *
 * Values are UPPERCASE to match `EmailProvider`/`AccountSource`/`InboxCategory`
 * — its neighbours in this file and in the same `emailAccounts` row, read in the
 * same breath. A deliberate, recorded deviation from 11-conventions.md:183
 * (docs/plans/gmail-app-password-connect.md §7.1).
 *
 * NOTE (conventions §9): adding a value needs a matching ALTER … MODIFY COLUMN
 * in packages/database/schema-changes/, or MySQL (non-strict) silently truncates
 * writes of the new value to '' and strands the rows.
 *
 * The `usesPasswordTransport` predicate over this enum lives in
 * `../utils/email.ts` — a `*.enum.ts` file holds declarations only (package
 * charter; see the note on `account-state.enum.ts`).
 */
export enum EmailAuthMethod {
  OAUTH = 'OAUTH',
  PASSWORD = 'PASSWORD',
}

export enum SmtpEncryptionType {
  SSL  = 'SSL',
  TLS  = 'TLS',
  NONE = 'NONE',
}

export enum AccountSource {
  DIRECT     = 'DIRECT',
  SALESHANDY = 'SALESHANDY',
  // Domain-wide-delegation bulk connect (one admin authorizes the whole
  // workspace/tenant; no per-mailbox OAuth). Values kept ≤20 chars to fit the
  // `emailAccounts.source` varchar(20) column.
  GWS_BULK   = 'GWS_BULK',
  MS_BULK    = 'MS_BULK',
  // Connected headlessly over the public API by a partner who holds the
  // mailbox's OAuth refresh token themselves. Descriptive only — the
  // load-bearing discriminator is `emailAccounts.partnerIntegrationId`, because
  // `source` is not written at all on the DWD import path above.
  PARTNER    = 'PARTNER',
}

/** @deprecated use `AccountState` from `account-state.enum.ts`. */
export enum AccountStatus {
  CONNECTED    = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
}

export enum InboxCategory {
  INBOX      = 'INBOX',
  PROMOTIONS = 'PROMOTIONS',
  SOCIAL     = 'SOCIAL',
  UPDATES    = 'UPDATES',
  SPAM       = 'SPAM',
  OTHER      = 'OTHER',
}

export type EspGroup = 'google' | 'microsoft' | 'other';
